import Keycloak from "keycloak-js";
import axios from "axios";

const KC_TOKEN_KEY = "kc_token";
const KC_REFRESH_KEY = "kc_refresh_token";
const KC_ID_TOKEN_KEY = "kc_id_token";

const _kc = new Keycloak({
  realm: process.env.REACT_APP_ONEVERSE_KEYCLOAK_REALM,
  url: process.env.REACT_APP_ONEVERSE_KEYCLOAK_URL,
  clientId: "re-agent",
});

const initKeycloak = (onAuthenticatedCallback) => {
  configureAuthInterceptors();
  const token = localStorage.getItem(KC_TOKEN_KEY) || undefined;
  const refreshToken = localStorage.getItem(KC_REFRESH_KEY) || undefined;
  const idToken = localStorage.getItem(KC_ID_TOKEN_KEY) || undefined;

  _kc
    .init({
      checkLoginIframe: false,
      token,
      refreshToken,
    })
    .then(() => {
      onAuthenticatedCallback();
    })
    .catch((err) => {
      console.error("Keycloak init error:", err);
      localStorage.removeItem(KC_TOKEN_KEY);
      localStorage.removeItem(KC_REFRESH_KEY);
      localStorage.removeItem(KC_ID_TOKEN_KEY);
      onAuthenticatedCallback();
    });
};

const loginWithCredentials = async (username, password) => {
  const url = `${process.env.REACT_APP_ONEVERSE_KEYCLOAK_URL}/realms/${process.env.REACT_APP_ONEVERSE_KEYCLOAK_REALM}/protocol/openid-connect/token`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "password",
      client_id: "re-agent",
      username,
      password,
    }),
  });
  const data = await response.json();

  if (!response.ok) {
    if (
      data.error === "invalid_grant" &&
      data.error_description?.includes("Account is not fully set up")
    ) {
      const err = new Error("first_login_required");
      err.code = "first_login_required";
      throw err;
    }
    throw new Error(data.error_description || "Invalid username or password");
  }

  localStorage.setItem(KC_TOKEN_KEY, data.access_token);
  localStorage.setItem(KC_REFRESH_KEY, data.refresh_token);
  if (data.id_token) localStorage.setItem(KC_ID_TOKEN_KEY, data.id_token);
  localStorage.removeItem("agencySlug");
  window.location.href = window.location.origin;
};

const doLogin = _kc.login;

const doLogout = () => {
  localStorage.removeItem(KC_TOKEN_KEY);
  localStorage.removeItem(KC_REFRESH_KEY);
  localStorage.removeItem(KC_ID_TOKEN_KEY);
  localStorage.removeItem("agencyDetails");
  window.location.href = window.location.origin;
};

const getToken = () => _kc.token;
const getTokenParsed = () => _kc.tokenParsed;
const isLoggedIn = () => !!_kc.token;
// Refresh the access token if it expires within `minValidity` seconds (no-op
// if still valid). Returns the keycloak-js promise (resolves true if refreshed,
// false if not needed; rejects if the refresh token itself is dead).
const updateToken = (minValidity = 30) => _kc.updateToken(minValidity);

// One-time install of axios auth interceptors. Fixes the "tab idle past the
// token lifetime → data won't load until I reload the address bar" bug: the
// reload worked only because _kc.init() silently refreshes via the stored
// refresh token. These interceptors do that same refresh automatically, so
// in-app navigation never fires a stale-token request.
let _interceptorsConfigured = false;
const configureAuthInterceptors = () => {
  if (_interceptorsConfigured) return;
  _interceptorsConfigured = true;

  // Refresh BEFORE every authenticated request goes out. Only touches requests
  // that already carry an Authorization header — public share-page calls (which
  // use no auth header) stay public, even if an agent is logged in elsewhere.
  axios.interceptors.request.use(async (config) => {
    const hasAuth = !!(config.headers && config.headers.Authorization);
    if (hasAuth && _kc.authenticated) {
      try {
        await _kc.updateToken(30);
      } catch (e) {
        // Refresh failed — let the request go and the response handler deal
        // with the 401 (force-refresh + retry, then login if truly dead).
      }
      config.headers.Authorization = `Bearer ${_kc.token}`;
    }
    return config;
  });

  // Safety net: an authenticated call that still 401s gets ONE forced refresh
  // + retry. If the refresh token is genuinely dead, send the user to login.
  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error?.config;
      const status = error?.response?.status;
      if (status === 401 && cfg && !cfg.__authRetried && _kc.authenticated) {
        cfg.__authRetried = true;
        try {
          await _kc.updateToken(-1); // -1 = force refresh regardless of validity
          cfg.headers.Authorization = `Bearer ${_kc.token}`;
          return axios(cfg);
        } catch (e) {
          doLogin();
        }
      }
      return Promise.reject(error);
    },
  );

  // Background tabs throttle JS timers, so the token often dies while away.
  // Refresh the moment the tab becomes visible again (kills the "come back to
  // the tab after a while" case before the user even clicks anything).
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && _kc.authenticated) {
        _kc.updateToken(60).catch(() => {});
      }
    });
  }
};
const getUsername = () => _kc.tokenParsed?.preferred_username;
const getUseremail = () => _kc.tokenParsed?.email;
const getUserNetwork = () => _kc.tokenParsed?.network;
const getFullName = () => _kc.tokenParsed?.name;
const checkRole = (roles) =>
  Array.isArray(roles) && roles.some((role) => _kc.hasRealmRole(role));
const hasRole = (role) => _kc.hasRealmRole(role);
// Keycloak realm roles are additive, so one person can legitimately carry
// BOTH `customer` and a staff role — e.g. someone who existed as a lead
// first and was later made an agent. A bare hasRole("customer") then wins
// and downgrades that user to the customer experience (Platform Analytics
// hidden, customer home page). Use this wherever "customer" is meant as an
// identity rather than a capability: it's true only when the user has no
// staff role at all.
const STAFF_ROLES = [
  "agent",
  "agency_system_admin",
  "agency_business_admin",
  "super_admin",
];
const isCustomerOnly = () =>
  _kc.hasRealmRole("customer") &&
  !STAFF_ROLES.some((role) => _kc.hasRealmRole(role));
const findRole = (role) =>
  _kc.realmAccess.roles.filter((temp) => role === temp);
const role = (role) => {
  return _kc.realmAccess?.roles.filter((temp) => role === temp);
};

const UserService = {
  initKeycloak,
  configureAuthInterceptors,
  loginWithCredentials,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  getTokenParsed,
  updateToken,
  getUsername,
  getUseremail,
  hasRole,
  isCustomerOnly,
  role,
  findRole,
  checkRole,
  getUserNetwork,
  getFullName,
};

export default UserService;
